import { useQuery } from '@tanstack/react-query';
import { DashboardApi } from '../api';
import type {
  DashboardLeaderboardMetricId,
  DashboardLeaderboardResponse,
  DashboardLeaderboardSortMode,
  DashboardTableFilterParams,
} from '../../interfaces/dashboardLeaderboardActivity';

const dashboardApi = new DashboardApi();
const inJest = typeof process !== 'undefined' && process.env.JEST_WORKER_ID != null;

export type UseDashboardLeaderboardParams = {
  metric: DashboardLeaderboardMetricId;
  sort: DashboardLeaderboardSortMode;
  filters: DashboardTableFilterParams;
  limit?: number;
  page?: number;
  enabled?: boolean;
};

export const buildDashboardLeaderboardQueryKey = (p: UseDashboardLeaderboardParams) =>
  [
    'dashboard-leaderboard',
    p.metric,
    p.sort,
    p.limit ?? 10,
    p.page ?? 1,
    p.filters.teamId ?? '',
    p.filters.rangePreset ?? '',
    p.filters.startDate ?? '',
    p.filters.endDate ?? '',
    p.filters.playersGroup ?? '',
    p.filters.playerNameSearch ?? '',
  ] as const;

export const useDashboardLeaderboard = ({
  metric,
  sort,
  filters,
  limit = 10,
  page = 1,
  enabled = true,
}: UseDashboardLeaderboardParams) => {
  return useQuery<DashboardLeaderboardResponse>({
    queryKey: buildDashboardLeaderboardQueryKey({ metric, sort, filters, limit, page }),
    queryFn: async () => {
      // In Jest we keep using the local mock so tests do not depend on
      // network/real backend data. In the app we call the real endpoint.
      const res = inJest
        ? await dashboardApi.getLeaderboardMock({ metric, sort, filters, limit, page })
        : await dashboardApi.getLeaderboard({ metric, sort, filters, limit, page });
      if (!res.status) throw new Error('Failed to load leaderboard');
      return res.data;
    },
    enabled,
  });
};
